import { FC, useCallback, useEffect } from 'react';
import { Typography, Box, Button } from '@mui/material';
import {
  ApiManagementForm,
  ApiManagementFormState,
  ExchangeCredentialState
} from './api-management-form';
import AddIcon from '@mui/icons-material/Add';
import { useApiManagementApi } from './api-management.api.hook';
import { useApiManagementState } from './api-management.hook';
import { useObservable } from '@client/foundation/shared/utils/observable.hook';
import { useWidget } from '@client/widgets/widget.hook';

export const ApiManagement: FC = () => {
  const { openWidget } = useWidget();

  const { settings, updateExchangeApiSettings, deleteExchangeApiSettings } =
    useApiManagementApi();

  const { initializeState, state$ } = useApiManagementState();
  const state = useObservable(() => state$);

  useEffect(() => {
    if (!settings) return;

    const exchanges: ApiManagementFormState['exchanges'] =
      settings.map(
        ({
          exchangeId,
          liveApiKeyPopulated,
          liveApiSecretPopulated,
          testnetApiKeyPopulated,
          testnetApiSecretPopulated
        }) => ({
          exchangeId,
          apiKeys: {
            liveApiKeyPopulated,
            liveApiSecretPopulated,
            testnetApiKeyPopulated,
            testnetApiSecretPopulated
          }
        })
      ) || [];

    initializeState({ exchanges });
  }, [settings]);

  const handleUpdateApiKeys = useCallback(
    async ({
      exchangeId,
      apiKeys
    }: ApiManagementFormState['exchanges'][number]) => {
      // Allowed fields that can be updated
      const allowedFields: (keyof ExchangeCredentialState)[] = [
        'liveApiKey',
        'liveApiSecret',
        'testnetApiKey',
        'testnetApiSecret'
      ];

      const fieldsToUpdate = Object.fromEntries(
        Object.entries(apiKeys || {}).filter(
          ([key, value]) =>
            Boolean(value) &&
            allowedFields.includes(key as keyof ExchangeCredentialState)
        )
      );

      updateExchangeApiSettings({
        exchangeId,
        ...fieldsToUpdate
      });
    },
    []
  );

  const handleOpenTradingForm = useCallback(() => {
    openWidget({ type: 'AddExchangeForm' });
  }, [openWidget]);

  const handleDeleteExchange = useCallback(async (exchangeId: string) => {
    deleteExchangeApiSettings({ exchangeId });
  }, []);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        API Management
      </Typography>
      <Typography variant="body1" gutterBottom>
        Configure API keys for different exchanges.
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="text"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenTradingForm}
          sx={{ mt: 3, mb: 3 }}
        >
          Add Exchange
        </Button>
      </Box>

      <ApiManagementForm
        state={state}
        onSubmit={handleUpdateApiKeys}
        onDelete={handleDeleteExchange}
      />
    </Box>
  );
};
